import * as React from 'react';
import { MouseEventHandler, Ref } from 'react';
import PropTypes from 'prop-types';
import { MenuItem, MenuItemProps } from '@mui/material';
import {
    useTranslate,
    RedirectionSideEffect,
    MutationMode,
    useResourceContext,
    useRecordContext,
} from 'react-admin';
import { useDeleteBranchWithUndoController } from './controllers';
import { TreeRecord } from './types';

export const DeleteMenuItemWithUndo = <
    RecordType extends TreeRecord = TreeRecord
>(
    props: DeleteMenuItemWithUndoProps<RecordType>
) => {
    const {
        className,
        label = 'ra.action.delete',
        mutationMode,
        onClick,
        record: recordProp,
        ref,
        resource: resourceProp,
        redirect: redirectTo = 'list',
        ...rest
    } = props;
    const resource = useResourceContext(props);
    const record = useRecordContext<RecordType>(props);
    const { handleDelete, isLoading } = useDeleteBranchWithUndoController({
        record,
        resource,
        redirect: redirectTo,
    });
    const translate = useTranslate();

    const handleClick: MouseEventHandler<HTMLLIElement> = event => {
        event.stopPropagation();
        if (onClick) {
            onClick(event);
        }

        if (!event.isDefaultPrevented()) {
            handleDelete(event);
        }
    };

    return (
        <MenuItem
            className={className}
            disabled={isLoading}
            onClick={handleClick}
            ref={ref}
            {...rest}
        >
            {translate(label, { _: label })}
        </MenuItem>
    );
};

export interface DeleteMenuItemWithUndoProps<
    RecordType extends TreeRecord = TreeRecord
> extends Omit<MenuItemProps, 'button' | 'id'> {
    label?: string;
    record?: RecordType;
    redirect?: RedirectionSideEffect;
    ref?: Ref<HTMLLIElement>;
    resource?: string;
    mutationMode?: MutationMode;
}

DeleteMenuItemWithUndo.propTypes = {
    className: PropTypes.string,
    label: PropTypes.string,
    record: PropTypes.any,
    // @ts-ignore
    redirect: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.bool,
        PropTypes.func,
    ]),
    resource: PropTypes.string,
};
